import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Box, TableCell, TableHead, TableRow, TableSortLabel, TextField, Tooltip } from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as actionsNV05 from '../../../store/actions/indexNV05';
import { AppState } from '../../../store/rootReducer';
import { useStyles } from '../styles/Head.styles';
import { TableHeaders } from '../../../models/Headers';



// Initial state for the column headers
// headerListInitialState
export const hInit: TableHeaders[] = [
  { foc: 0, isSort: 0, isActive: 0, isFilter: 1, filter: '', vis: 1, width: '10%', sortDirection: 'asc', id: "varegruppe", lb: "Varegruppe" },
  { foc: 0, isSort: 0, isActive: 0, isFilter: 1, filter: '', vis: 1, width: '40%', sortDirection: 'desc', id: "varegruppetekst", lb: "Beskrivelse" },
  { foc: 0, isSort: 0, isActive: 0, isFilter: 0, filter: '', vis: 1, width: '20%', sortDirection: 'asc', id: "salgHitMnd", lb: "Salg denne mnd.", align: "right" },
  { foc: 0, isSort: 0, isActive: 0, isFilter: 0, filter: '', vis: 1, width: '15%', sortDirection: 'asc', id: "salgHitFjor", lb: "Salg i år", align: "right" },
  { foc: 0, isSort: 0, isActive: 0, isFilter: 0, filter: '', vis: 1, width: '15%', sortDirection: 'asc', id: "salgHitAr", lb: "Salg i fjor", align: "right" },
];


// Props interface
interface NV05TableHeadProps { onRequestSort: (property: TableHeaders[]) => void; }


// Define the form input types
type FormInputs = {
  navn: any
  [key: string]: string // Add a catch-all key of type string
};


// Component
const NV05TableHead: React.FC<NV05TableHeadProps> = ({ onRequestSort }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const tableHeaders: TableHeaders[] = useSelector((state) => (state as AppState).NV05.tableHeaders);
  const isReset: boolean = useSelector((state) => (state as AppState).NV05.isReset);
  // React Hook Form configuration with default values
  const methods = useForm<FormInputs>({
    defaultValues: {
      navn: '',
    },
  });

  
  //
  useEffect(() => {
    tableHeaders.forEach(element => {
      // Always set the value, even if it's empty
      methods.setValue(element.id, element.filter ?? '');
    });
    // Reset the reset flag
    if (isReset) {
      dispatch(actionsNV05.NV05ResetForm(false));
    }
    //
  }, [dispatch, tableHeaders, isReset]);

  
  // Handle the click event on the column header
  const handleClick = (property: TableHeaders) => {
    const filteredColumnHeaderList: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id) {
        return {
          ...row,
          isActive: 1,
          sortDirection: row.sortDirection === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        ...row,
        isActive: 0,
        sortDirection: 'desc',
      };
    });
    // Update the redux store with the new column header list
    dispatch(actionsNV05.NV05UpdateColumnHeaderList(filteredColumnHeaderList));

    // Call the parent component to sort the data
    if (onRequestSort) {
      onRequestSort(filteredColumnHeaderList);
    }
  };

 
  // Handle the click event on the column header
  const handleFieldBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    property: TableHeaders) => {
    const filteredColumnHeaderList: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id && (e.target as HTMLInputElement).value === '') {
        return {
          ...row,
          foc: 0,
        };
      }
      return row;
    });
    // Update the redux store with the new column header list
    dispatch(actionsNV05.NV05UpdateColumnHeaderList(filteredColumnHeaderList));
  };

  
  // Handle the click event on the column header
  const handleFieldFocus = (_e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    property: TableHeaders) => {
    const filteredColumnHeaderList: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id) {
        return {
          ...row,
          foc: 1,
        };
      }
      return row;
    });
    // Set the focus on the input field
    const inputField: HTMLInputElement | null = document.querySelector('input[name="' + property.id + '"]');
    if (inputField) {
      inputField.focus();
    }
    // Update the redux store with the new column header list
    dispatch(actionsNV05.NV05UpdateColumnHeaderList(filteredColumnHeaderList));
  };

  
  // Handle the click event on the column header
  interface HandleChangeEvent extends React.KeyboardEvent<HTMLInputElement> {
    target: HTMLInputElement;
  }

  const handleChange = (
    _e: React.KeyboardEvent<HTMLDivElement>,
    property: TableHeaders
  ): void => {
    const { key } = _e; // Get the key pressed
    const inputValue = (_e.target as HTMLInputElement).value;
    const actionSource = key || inputValue; // Get the key pressed or the value entered in the input field
    methods.setValue(property.id as string, inputValue);
    _e.stopPropagation(); // Prevent the event from bubbling up
    _e.preventDefault(); // Prevent the default action of the event from happening
    //
    const filteredColumnHeaderList: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id) {
        return {
          ...row,
          filter: inputValue,
        };
      }
      return row;
    });
    dispatch(actionsNV05.NV05UpdateColumnHeaderList(filteredColumnHeaderList));
    // Update the redux store with the new column header list and submit the request to sort the data
    if (onRequestSort && actionSource === 'Enter') {
      onRequestSort(filteredColumnHeaderList);
    }
  };

  
  // Handle the click event on the column header
  const handleLabelClicked = (e: React.MouseEvent, property: TableHeaders) => {
    methods.setValue(property.id as string, '');
    // Update the label state to remove the [X] prefix if it exists
    const updatedHeaders: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id) {
        return {
          ...row,
          filter: '',
          foc: 0,
        };
      }
      return row;
    });
    // Dispatch the updated headers to the redux store
    dispatch(actionsNV05.NV05UpdateColumnHeaderList(updatedHeaders));
    onRequestSort(updatedHeaders);
  };

  
  // Handle the click event on the column header
  interface LabelFilterClickEvent extends React.MouseEvent<SVGSVGElement, MouseEvent> { }

  const handleLabelFilterClicked = (
    e: LabelFilterClickEvent,
    property: TableHeaders
  ): void => {
    //
    const isFocused: boolean = property.foc === 1;
    //
    const updatedHeaders: TableHeaders[] = tableHeaders.map((row: TableHeaders) => {
      if (row.id === property.id) {
        return {
          ...row,
          foc: isFocused ? 0 : 1,
          filter: isFocused ? '' : row.filter, // Ensure the `filter` property matches the expected type
        };
      }
      return row;
    });

    if (!isFocused) {
      // Set focus on the input field
      const inputField: HTMLInputElement | null = document.querySelector<HTMLInputElement>(`input[name="${property.id}"]`);
      inputField?.focus();
    } else {
      methods.setValue(property.id as string, '');
    }

    // Dispatch the updated headers to the redux store
    dispatch(actionsNV05.NV05UpdateColumnHeaderList(updatedHeaders));
    // Call the parent component to sort the data
    if (isFocused) {
      onRequestSort(updatedHeaders);
    }
  };

  
  /*
  * Build the menu items parent nodes
  */
  const renderColumnHeader = () => {
    const JSXColumnHeader: React.JSX.Element[] = [];
    tableHeaders.forEach((row: TableHeaders) => {
      const label = row.lb;
      const colAlign = hInit.find(h => h.id === row.id)?.align || "left";
      switch (true) {
        default: {
          row.vis === 1 ? JSXColumnHeader.push(
            // Your TableCell component
            <TableCell
              className={classes.tableCell}
              key={row.id}
              align={colAlign}
              padding="none"
              style={{ textAlign: colAlign }}
            >
              {row.isSort || row.isFilter ? (
                <Tooltip
                  title={'Skriv inn og trykk ENTER for å filtrere etter ' + label}
                  enterDelay={300}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: 15,
                        fontWeight: 100,
                        color: '#fff',
                      },
                    },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <TextField
                      id={row.id}
                      label={label}
                      autoComplete="off"
                      variant="standard"
                      autoSave='off'
                      placeholder={methods.getValues(row.id as string)}
                      {...methods.register(row.id as string, { required: false })}
                      onFocus={(e) => handleFieldFocus(e, row)}
                      onBlur={(e) => handleFieldBlur(e, row)}
                      onKeyUp={(e) => { handleChange(e, row); }}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          height: '15px',
                          padding: '0px 0',
                          color: '#000',
                          '&:before': {
                            borderBottom: '1px solid lightgray',
                          },
                          '&:after': {
                            borderBottom: '1px solid lightgray',
                          },
                          '&:hover:not(.Mui-disabled):before': {
                            borderBottom: '1px solid gray',
                          },
                        },
                      }}
                      sx={{
                        width: '95%',
                        '& .MuiInputLabel-root': {
                          top: '-16px',
                          fontWeight: 500,
                        },
                        '& .MuiInputLabel-shrink': {
                          color: '#000',
                          top: '-3px',
                          fontWeight: 400,
                        },
                        '& .MuiInputBase-root': {
                          marginBottom: '0px',
                        },
                      }}
                      InputLabelProps={{
                        shrink: row.foc === 1 ? true : false,
                        onClick: (e) => {
                          handleLabelClicked(e, row);
                        },
                        sx: {
                          '&.Mui-focused': {
                            cursor: 'pointer',
                            color: '#000',
                            fontSize: 17,
                          },
                        },
                      }}
                    />
                    <TableSortLabel
                      active={row.isActive === 1 ? true : false}
                      direction={row.sortDirection}
                      onClick={() => handleClick(row)}
                      hideSortIcon={true}
                    /> <FilterAltIcon onClick={(e) => handleLabelFilterClicked(e, row)}
                      sx={{
                        cursor: 'pointer',
                        color: '#darkgray',
                        transform: row.foc === 1 ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        '&:before': { borderBottom: '1px solid lightgray', },
                        '&:after': { borderBottom: '1px solid lightgray', },
                        '&:hover:not(.Mui-disabled):before': { borderBottom: '1px solid gray', },
                      }}
                    />
                  </Box>
                </Tooltip>
              ) : (
                <span style={{ textAlign: colAlign, display: "block", paddingRight: '8px' }}>{label}</span>
              )}
            </TableCell>
          ) : null
        }
          break;
      }
    });
    //
    return JSXColumnHeader;
  };
  //
  return (
    <TableHead><TableRow>{renderColumnHeader()}</TableRow></TableHead>
  );
};

export default NV05TableHead;